import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Drawer,
  Grid,
  Group,
  Image,
  MultiSelect,
  NumberInput,
  Paper,
  SegmentedControl,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ART_FORM_GROUP_LABELS,
  ART_FORMS,
  type ArtFormGroup,
  VENUE_TYPE_GROUP_LABELS,
  VENUE_TYPES,
  type VenueTypeGroup,
  VIBE_TAGS,
} from '@wwaave/shared/vocab';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { PublicShell } from '../../components/marketing/PublicShell';
import { JoinLink, MotionSection, PageHero } from '../../components/ui';
import { PageFrame } from '../../components/ui/PageFrame';
import { ApiError, fetchJson } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { track } from '../../lib/telemetry';

// Dual-shell (see the venue/artist detail precedent, FR-144): anonymous
// visitors get the marketing chrome so the top nav — including the "How It
// Works" tab — is present on /browse, not the app sidebar (feedback Y_32Y).
// Stable module-scope wrappers so the shell identity doesn't change per render.
function BrowseAuthedShell({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <AppShell tone="content">{children}</AppShell>;
}
function BrowseAnonShell({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <PublicShell>
      <PageFrame tone="content">{children}</PageFrame>
    </PublicShell>
  );
}

/**
 * Early-access band for anonymous browsers. Browsing is the moment a visitor
 * pictures themselves in a room, so it's the right place to ask — and it asks
 * once, in a band, rather than gating the page. JoinLink sends them to
 * /earlyaccess pre-launch and /sign-up once sign-ups open.
 */
function BrowseEarlyAccessCta(): React.JSX.Element {
  return (
    <Box
      style={{
        borderRadius: 16,
        border: '1px solid var(--mantine-color-ink-2)',
        backgroundColor: 'var(--wwaave-card-bg)',
        padding: '20px 24px',
      }}
    >
      <Group justify="space-between" align="center" wrap="wrap" gap="md">
        <Stack gap={4} style={{ flex: 1, minWidth: 240 }}>
          <Text fw={700} size="lg" c="var(--mantine-color-ink-9)">
            Want to reach out to these venues?
          </Text>
          <Text size="sm" c="dimmed">
            Be the first to know when sign-ups open.
          </Text>
        </Stack>
        <JoinLink>
          <Button
            component="span"
            tabIndex={-1}
            size="md"
            radius="md"
            variant="gradient"
            gradient={{ from: 'brand.6', to: 'sun.6', deg: 35 }}
          >
            Get Early Access
          </Button>
        </JoinLink>
      </Group>
    </Box>
  );
}

// Lazy-load the leaflet map: keeps leaflet (~45 KB gz) + leaflet CSS off the
// initial /browse bundle, and ensures `import 'leaflet'` (which touches
// `window` at module-eval time) never runs anywhere it shouldn't.
const VenueMap = lazy(() => import('../../components/browse/VenueMap'));

type MapBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

// ─── Types (mirror worker DTOs; intentionally local to keep the client-side
// surface free of worker-side imports) ──────────────────────────────────────

type VenueCardDTO = {
  id: string;
  slug: string;
  name: string;
  summary: string | null;
  city: string;
  region: string | null;
  country: string;
  lat: number;
  lng: number;
  capacityMin: number;
  capacityMax: number;
  stipendOffered: boolean;
  verified: boolean;
  foundingMember: boolean;
  /** Wave 5: account-level founding flag mirrored from venue_accounts. */
  foundingHostAccount: boolean;
  heroUrl: string | null;
  reviewAverage: number | null;
  reviewCount: number;
  /**
   * Wave 4M — count of open (published) events at this venue. Drives the
   * "N open events" chip on each venue card. 0 hides the chip entirely.
   */
  openEventsCount: number;
};

type VenueSearchResult = {
  results: VenueCardDTO[];
  next_cursor: string | null;
  total_estimate: number;
};

type SmartMatchVenue = VenueCardDTO & {
  matchScore: number;
  matchReasons: string[];
};

type SmartMatchResult = {
  venues: SmartMatchVenue[];
  nextCursor: string | null;
};

type BrowseSearch = {
  q?: string | undefined;
  art_form?: string[] | undefined;
  venue_type?: string[] | undefined;
  vibe_tag?: string[] | undefined;
  capacity_min?: number | undefined;
  capacity_max?: number | undefined;
  verified?: boolean | undefined;
  has_stipend?: boolean | undefined;
  sleep?: boolean | undefined;
  sort?: 'relevance' | 'recently_active' | 'highest_reviewed' | undefined;
  view?: 'list' | 'map' | undefined;
};

export const Route = createFileRoute('/browse/')({
  validateSearch: (search: Record<string, unknown>): BrowseSearch => {
    const out: BrowseSearch = {};
    if (typeof search.q === 'string' && search.q.length > 0) out.q = search.q;
    if (Array.isArray(search.art_form)) {
      out.art_form = search.art_form.filter((s): s is string => typeof s === 'string');
    } else if (typeof search.art_form === 'string') {
      out.art_form = [search.art_form];
    }
    if (Array.isArray(search.venue_type)) {
      out.venue_type = search.venue_type.filter((s): s is string => typeof s === 'string');
    } else if (typeof search.venue_type === 'string') {
      out.venue_type = [search.venue_type];
    }
    if (Array.isArray(search.vibe_tag)) {
      out.vibe_tag = search.vibe_tag.filter((s): s is string => typeof s === 'string');
    } else if (typeof search.vibe_tag === 'string') {
      out.vibe_tag = [search.vibe_tag];
    }
    if (typeof search.capacity_min === 'number') out.capacity_min = search.capacity_min;
    if (typeof search.capacity_max === 'number') out.capacity_max = search.capacity_max;
    if (search.verified === true || search.verified === 'true') out.verified = true;
    if (search.has_stipend === true || search.has_stipend === 'true') out.has_stipend = true;
    if (search.sleep === true || search.sleep === 'true') out.sleep = true;
    if (
      search.sort === 'relevance' ||
      search.sort === 'recently_active' ||
      search.sort === 'highest_reviewed'
    ) {
      out.sort = search.sort;
    }
    if (search.view === 'list' || search.view === 'map') out.view = search.view;
    return out;
  },
  component: BrowsePage,
});

function buildQueryString(s: BrowseSearch, cursor: string | null | undefined): string {
  const params = new URLSearchParams();
  if (s.q) params.set('q', s.q);
  if (s.sort) params.set('sort', s.sort);
  if (typeof s.capacity_min === 'number') params.set('capacity_min', String(s.capacity_min));
  if (typeof s.capacity_max === 'number') params.set('capacity_max', String(s.capacity_max));
  if (s.verified) params.set('verified', '1');
  if (s.has_stipend) params.set('has_stipend', '1');
  if (s.sleep) params.set('sleep', '1');
  for (const slug of s.art_form ?? []) params.append('art_form', slug);
  for (const slug of s.venue_type ?? []) params.append('venue_type', slug);
  for (const slug of s.vibe_tag ?? []) params.append('vibe_tag', slug);
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

async function fetchBrowse(s: BrowseSearch, cursor: string | null | undefined): Promise<VenueSearchResult> {
  const qs = buildQueryString(s, cursor);
  // fetchOk (not bare fetch) so the status survives onto the thrown error —
  // a 403 here means "browse isn't open to you yet", which is a different
  // message from "something broke". See BrowseNotOpenYet below.
  return fetchJson<VenueSearchResult>(`/api/browse${qs ? `?${qs}` : ''}`);
}

/** True when the venue search was refused by the `public_browse` gate, not by a fault. */
function isBrowseClosed(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/**
 * Shown when `/api/browse` returns 403 — the `public_browse` flag isn't open
 * to this caller yet. Nothing is broken, so the old shared "Couldn't load
 * venues right now. Refresh and try again." card was telling every pre-launch
 * visitor a falsehood and inviting them to retry something that will never
 * succeed. Say what's actually true and offer the one action that helps.
 */
function BrowseNotOpenYet(): React.JSX.Element {
  return (
    <Paper p="xl" withBorder>
      <Stack gap="sm" align="center">
        <Text fw={600} size="lg">
          The venue list opens with early access.
        </Text>
        <Text size="sm" c="dimmed" ta="center" maw={460}>
          Founding venues are still being welcomed in. Join the early-access list and
          you&apos;ll be among the first through the door.
        </Text>
        <JoinLink>
          <Button
            component="span"
            tabIndex={-1}
            size="md"
            radius="md"
            variant="gradient"
            gradient={{ from: 'brand.6', to: 'sun.6', deg: 35 }}
          >
            Get Early Access
          </Button>
        </JoinLink>
      </Stack>
    </Paper>
  );
}

/** Generic load failure — a real fault, where retrying is the right advice. */
function BrowseLoadError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  return (
    <Paper p="xl" withBorder>
      <Stack gap="xs" align="center">
        <Text>Couldn&apos;t load venues right now. Refresh and try again.</Text>
        <Button variant="default" onClick={onRetry}>
          Try again
        </Button>
      </Stack>
    </Paper>
  );
}

async function fetchSmartMatch(cursor: string | null | undefined): Promise<SmartMatchResult> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  const qs = params.toString();
  return fetchJson<SmartMatchResult>(`/api/browse/smart-match${qs ? `?${qs}` : ''}`);
}

// ─── Component ──────────────────────────────────────────────────────────────

function BrowsePage(): React.JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/browse/' });
  const [filtersOpen, filtersCtl] = useDisclosure(false);

  // Local working copy of the search input — only commits to URL on submit.
  const [qInput, setQInput] = useState(search.q ?? '');

  const view = search.view ?? 'list';

  // Bounds-filter state for the map view. We keep the FULL venue set in the
  // map (so panning never makes pins vanish that the user is heading toward)
  // and only filter the result list below the map. Set on `moveend` from
  // VenueMap; cleared whenever the view leaves 'map' so a return to list
  // doesn't carry stale filtering.
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  // Mobile-only toggle so the user can hide the map and see the full list.
  const [mobileShowList, setMobileShowList] = useState(false);

  const { capabilities, user } = useAuth();
  const isArtist = capabilities?.isArtist === true;
  const Shell = user === null ? BrowseAnonShell : BrowseAuthedShell;
  const [smartMatchActive, setSmartMatchActive] = useState(false);

  const smartMatchQuery = useInfiniteQuery({
    queryKey: ['browse', 'smart-match'],
    queryFn: ({ pageParam }: { pageParam: string | null | undefined }) =>
      fetchSmartMatch(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: SmartMatchResult) => lastPage.nextCursor,
    enabled: smartMatchActive && isArtist,
    placeholderData: keepPreviousData,
  });

  const query = useInfiniteQuery({
    queryKey: ['browse', search],
    queryFn: ({ pageParam }: { pageParam: string | null | undefined }) =>
      fetchBrowse(search, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: VenueSearchResult) => lastPage.next_cursor,
    placeholderData: keepPreviousData,
    enabled: !smartMatchActive,
    // A 403 is a settled answer, not a blip — retrying it three times with
    // backoff just left the page on a blank skeleton for ~10s before finally
    // showing anything at all.
    retry: (failureCount, error) => !isBrowseClosed(error) && failureCount < 3,
  });

  const smartMatchVenues = useMemo(
    () => smartMatchQuery.data?.pages.flatMap((p) => p.venues) ?? [],
    [smartMatchQuery.data],
  );

  const cards = useMemo(
    () => query.data?.pages.flatMap((p) => p.results) ?? [],
    [query.data],
  );
  const total = query.data?.pages[0]?.total_estimate ?? 0;

  // Cards filtered to the visible map bounds. Used only when view==='map'
  // and a bounds value has been published. Outside map view we pass `cards`
  // through unchanged.
  const cardsInBounds = useMemo(() => {
    if (view !== 'map' || mapBounds === null) return cards;
    return cards.filter(
      (c) =>
        c.lat <= mapBounds.north &&
        c.lat >= mapBounds.south &&
        c.lng <= mapBounds.east &&
        c.lng >= mapBounds.west,
    );
  }, [cards, view, mapBounds]);

  const updateSearch = (patch: Partial<BrowseSearch>): void => {
    // Emit per-key telemetry. `view` flips fire as map_view_toggled; all other
    // search keys fire as browse.filter_applied so the surface roll-up shows
    // which dimensions hosts/artists actually use. resultCount uses the
    // current loaded count — it's a lower bound until the next page settles
    // but a good signal for "did this filter return anything".
    for (const key of Object.keys(patch) as Array<keyof BrowseSearch>) {
      if (key === 'view') {
        const nextView = patch.view ?? 'list';
        track('browse.map_view_toggled', { view: nextView });
      } else {
        track('browse.filter_applied', {
          filterKey: key,
          resultCount: cards.length,
        });
      }
    }
    void navigate({
      search: (prev) => ({ ...prev, ...patch }),
    });
  };

  const onSubmitQuery = (e: React.FormEvent): void => {
    e.preventDefault();
    const trimmed = qInput.trim();
    updateSearch({ q: trimmed || undefined });
  };

  // Filter chip data for the URL summary line.
  const activeFilters: { key: keyof BrowseSearch; label: string; clear: () => void }[] = [];

  // Zero-results telemetry — fire once when a query settles with zero cards
  // AND there are active filters (anonymous "no venues exist" doesn't count).
  // Keyed off the search payload so we don't double-fire on re-renders.
  const lastZeroKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (query.isLoading || query.isFetching) return;
    if (cards.length > 0) return;
    const filterKeys: Array<keyof BrowseSearch> = [
      'q',
      'art_form',
      'venue_type',
      'vibe_tag',
      'capacity_min',
      'capacity_max',
      'verified',
      'has_stipend',
      'sleep',
    ];
    const active = filterKeys.filter((k) => {
      const v = search[k];
      if (Array.isArray(v)) return v.length > 0;
      return v !== undefined && v !== false && v !== '';
    });
    if (active.length === 0) return;
    const key = JSON.stringify({ s: search, c: cards.length });
    if (lastZeroKeyRef.current === key) return;
    lastZeroKeyRef.current = key;
    track('browse.filter_zero_results', { activeFilterCount: active.length });
  }, [cards.length, query.isLoading, query.isFetching, search]);

  // Pagination — emit a page number based on the number of pages loaded so far.
  const loadedPages = query.data?.pages.length ?? 0;
  if (search.verified) {
    activeFilters.push({ key: 'verified', label: 'Verified only', clear: () => updateSearch({ verified: undefined }) });
  }
  if (search.has_stipend) {
    activeFilters.push({ key: 'has_stipend', label: 'Stipend offered', clear: () => updateSearch({ has_stipend: undefined }) });
  }
  if (search.sleep) {
    activeFilters.push({ key: 'sleep', label: 'Place to sleep', clear: () => updateSearch({ sleep: undefined }) });
  }
  if (search.art_form && search.art_form.length > 0) {
    for (const slug of search.art_form) {
      const af = ART_FORMS.find((x) => x.slug === slug);
      activeFilters.push({
        key: 'art_form',
        label: af?.name ?? slug,
        clear: () => updateSearch({ art_form: search.art_form?.filter((s) => s !== slug) }),
      });
    }
  }
  if (search.venue_type && search.venue_type.length > 0) {
    for (const slug of search.venue_type) {
      const vt = VENUE_TYPES.find((x) => x.slug === slug);
      activeFilters.push({
        key: 'venue_type',
        label: vt?.name ?? slug,
        clear: () => updateSearch({ venue_type: search.venue_type?.filter((s) => s !== slug) }),
      });
    }
  }
  if (search.vibe_tag && search.vibe_tag.length > 0) {
    for (const slug of search.vibe_tag) {
      const tg = VIBE_TAGS.find((x) => x.slug === slug);
      activeFilters.push({
        key: 'vibe_tag',
        label: tg?.name ?? slug,
        clear: () => updateSearch({ vibe_tag: search.vibe_tag?.filter((s) => s !== slug) }),
      });
    }
  }

  return (
    <Shell>
      <Stack gap="lg">
        {/* Header strip */}
        <MotionSection>
          <PageHero
            eyebrow="Find your next room"
            title="Browse venues"
            lede={
              smartMatchActive
                ? `${smartMatchVenues.length} ${smartMatchVenues.length === 1 ? 'venue' : 'venues'} matched to your profile.`
                : total > 0
                  ? `${total} ${total === 1 ? 'space' : 'spaces'} open to touring artists${
                      search.verified ? ', verified only' : ''
                    }.`
                  : 'Listening rooms, late-night stages, quiet weekend spaces. All open to exchange.'
            }
            action={
              <Group gap="xs">
                {isArtist && (
                  <Switch
                    label="Smart Match"
                    size="md"
                    checked={smartMatchActive}
                    onChange={(e) => {
                      const next = e.currentTarget.checked;
                      setSmartMatchActive(next);
                      track('browse.smart_match_toggled', { active: next });
                    }}
                  />
                )}
                <SegmentedControl
                  size="sm"
                  value={view}
                  onChange={(v) => updateSearch({ view: v === 'map' ? 'map' : 'list' })}
                  data={[
                    { label: 'List', value: 'list' },
                    { label: 'Map', value: 'map' },
                  ]}
                />
                {!smartMatchActive && (
                  <Button variant="default" onClick={filtersCtl.open} radius="md">
                    Narrow it down
                  </Button>
                )}
              </Group>
            }
          />
        </MotionSection>

        {/* Anonymous browsers get the early-access CTA. They can look around all
            they like; this is the one place on the page that asks them to act,
            and it points at the same /earlyaccess capture every other CTA does. */}
        {user === null && !isBrowseClosed(query.error) && <BrowseEarlyAccessCta />}

        {/* Search bar + sort — hidden when Smart Match is active */}
        {!smartMatchActive && (
          <Group gap="sm" align="flex-end" wrap="wrap">
            <form onSubmit={onSubmitQuery} style={{ flex: 1, minWidth: 240 }}>
              <TextInput
                placeholder="Search by name, city, or description"
                value={qInput}
                onChange={(e) => setQInput(e.currentTarget.value)}
                aria-label="Search venues"
                leftSection={<SearchIcon />}
              />
            </form>
            <SegmentedControl
              size="sm"
              value={search.sort ?? (search.q ? 'relevance' : 'recently_active')}
              onChange={(v) =>
                updateSearch({
                  sort:
                    v === 'relevance' || v === 'recently_active' || v === 'highest_reviewed'
                      ? v
                      : undefined,
                })
              }
              data={[
                { label: 'Relevance', value: 'relevance', disabled: !search.q },
                { label: 'Newest', value: 'recently_active' },
                { label: 'Top reviewed', value: 'highest_reviewed' },
              ]}
            />
          </Group>
        )}

        {/* Active filter chips — hidden when Smart Match is active */}
        {!smartMatchActive && activeFilters.length > 0 && (
          <Group gap="xs" wrap="wrap">
            {activeFilters.map((f, i) => (
              <Badge
                key={`${f.key}-${i}`}
                variant="light"
                size="lg"
                rightSection={
                  <ActionIcon
                    size="xs"
                    color="gray"
                    radius="xl"
                    variant="transparent"
                    onClick={f.clear}
                    aria-label={`Remove ${f.label} filter`}
                  >
                    ×
                  </ActionIcon>
                }
              >
                {f.label}
              </Badge>
            ))}
          </Group>
        )}

        {/* Body */}
        {smartMatchActive ? (
          <SmartMatchResults
            venues={smartMatchVenues}
            isLoading={smartMatchQuery.isLoading}
            isError={smartMatchQuery.isError}
            hasNextPage={smartMatchQuery.hasNextPage}
            isFetchingNextPage={smartMatchQuery.isFetchingNextPage}
            onLoadMore={() => void smartMatchQuery.fetchNextPage()}
            onRetry={() => void smartMatchQuery.refetch()}
          />
        ) : view === 'map' ? (
          query.isLoading ? (
            <Skeleton height={600} radius="md" />
          ) : query.isError ? (
            isBrowseClosed(query.error) ? (
              <BrowseNotOpenYet />
            ) : (
              <BrowseLoadError onRetry={() => void query.refetch()} />
            )
          ) : (
            <MapView
              venues={cards}
              cardsInBounds={cardsInBounds}
              onBoundsChange={setMapBounds}
              mobileShowList={mobileShowList}
              onToggleMobileList={() => setMobileShowList((v) => !v)}
              hasNextPage={query.hasNextPage}
              isFetchingNextPage={query.isFetchingNextPage}
              onLoadMore={() => {
                track('browse.load_more_clicked', { pageNumber: loadedPages + 1 });
                void query.fetchNextPage();
              }}
              verifiedOnly={search.verified === true}
            />
          )
        ) : query.isLoading ? (
          <ResultsSkeleton />
        ) : query.isError ? (
          isBrowseClosed(query.error) ? (
            <BrowseNotOpenYet />
          ) : (
            <BrowseLoadError onRetry={() => void query.refetch()} />
          )
        ) : cards.length === 0 ? (
          <EmptyResults verifiedOnly={search.verified === true} />
        ) : (
          <>
            <Grid gap="md">
              {cards.map((c, i) => (
                <Grid.Col key={c.id} span={{ base: 12, sm: 6, lg: 4 }}>
                  <VenueCard
                    card={c}
                    position={i}
                    totalLoaded={cards.length}
                    hasFilters={activeFilters.length > 0}
                  />
                </Grid.Col>
              ))}
            </Grid>
            {query.hasNextPage && (
              <Center>
                <Button
                  variant="default"
                  onClick={() => {
                    track('browse.load_more_clicked', { pageNumber: loadedPages + 1 });
                    void query.fetchNextPage();
                  }}
                  loading={query.isFetchingNextPage}
                >
                  Load more
                </Button>
              </Center>
            )}
          </>
        )}
      </Stack>

      <Drawer
        opened={filtersOpen}
        onClose={filtersCtl.close}
        position="right"
        size="md"
        title="Narrow it down"
      >
        <FilterPanel search={search} onApply={updateSearch} onClose={filtersCtl.close} />
      </Drawer>
    </Shell>
  );
}

// ─── Placeholder helpers ────────────────────────────────────────────────────

/** Brand palette for venue cards without a cover photo (Clay / Tide / Sun / Ink). */
const PLACEHOLDER_COLORS = [
  '#d2602e', '#2a7e78', '#a33b00', '#00605b', '#6c2300', '#00413d',
  '#aa6e00', '#45403a', '#684100', '#235e59',
];

/** Deterministic index derived from the venue name. */
function placeholderColorIndex(name: string): number {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return sum % PLACEHOLDER_COLORS.length;
}

/** First letter of each word (max 2). */
function venueInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function SearchIcon(): React.JSX.Element {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function SmartMatchResults({
  venues,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onRetry,
}: {
  venues: SmartMatchVenue[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
}): React.JSX.Element {
  if (isLoading) return <ResultsSkeleton />;
  if (isError) {
    return (
      <Paper p="xl" withBorder>
        <Stack gap="xs" align="center">
          <Text>Couldn&apos;t load Smart Match results right now.</Text>
          <Button variant="default" onClick={onRetry}>
            Try again
          </Button>
        </Stack>
      </Paper>
    );
  }
  if (venues.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Stack gap="xs" align="center">
          <Text fw={500}>No matches found yet.</Text>
          <Text size="sm" c="dimmed" ta="center">
            Complete your profile with art forms and a home location to improve results.
          </Text>
        </Stack>
      </Paper>
    );
  }
  return (
    <>
      <Grid gap="md">
        {venues.map((v, i) => (
          <Grid.Col key={v.id} span={{ base: 12, sm: 6, lg: 4 }}>
            <VenueCard
              card={v}
              position={i}
              totalLoaded={venues.length}
              hasFilters={false}
              matchScore={v.matchScore}
              matchReasons={v.matchReasons}
            />
          </Grid.Col>
        ))}
      </Grid>
      {hasNextPage && (
        <Center>
          <Button variant="default" onClick={onLoadMore} loading={isFetchingNextPage}>
            Load more
          </Button>
        </Center>
      )}
    </>
  );
}

function VenueCard({
  card,
  position,
  totalLoaded,
  hasFilters,
  matchScore,
  matchReasons,
}: {
  card: VenueCardDTO;
  position: number;
  totalLoaded: number;
  hasFilters: boolean;
  matchScore?: number;
  matchReasons?: string[];
}): React.JSX.Element {
  const place = [card.city, card.region, card.country].filter(Boolean).join(', ');
  // FR-142: fall back to the local initials placeholder (below) on load
  // failure rather than a third-party placehold.co box.
  const [imgErrored, setImgErrored] = useState(false);
  return (
    <Link
      to="/venues/$slug"
      params={{ slug: card.slug }}
      style={{ textDecoration: 'none', color: 'inherit', height: '100%', display: 'block' }}
      onClick={() => {
        track('browse.card_clicked', { position, totalLoaded, hasFilters });
      }}
    >
    <Card
      withBorder
      padding="0"
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '--card-hover-transform': 'translateY(-3px)',
        '--card-hover-shadow': '0 8px 24px -4px color-mix(in srgb, var(--wwaave-brand-primary) 10%, transparent), 0 4px 12px -2px rgba(233, 162, 59, 0.08)',
      } as React.CSSProperties}
      className="wwaave-card-hover"
    >
      <Card.Section style={{ position: 'relative' }}>
        {card.heroUrl && !imgErrored ? (
          <Image
            src={card.heroUrl}
            alt={card.name}
            h={180}
            fit="cover"
            loading="lazy"
            decoding="async"
            onError={() => setImgErrored(true)}
          />
        ) : (
          <Box
            h={180}
            style={{
              backgroundColor: `light-dark(${PLACEHOLDER_COLORS[placeholderColorIndex(card.name)]}, ${PLACEHOLDER_COLORS[placeholderColorIndex(card.name)]}cc)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text
              fw={700}
              fz={48}
              c="#fff"
              style={{ userSelect: 'none', letterSpacing: 2, lineHeight: 1 }}
            >
              {venueInitials(card.name)}
            </Text>
          </Box>
        )}
        {typeof matchScore === 'number' && (
          <Badge
            size="lg"
            variant="filled"
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              zIndex: 3,
              background: 'var(--wwaave-gradient)',
              color: '#fff',
              fontWeight: 700,
            }}
          >
            {matchScore}%
          </Badge>
        )}
        {(card.verified || card.foundingMember || card.foundingHostAccount) && (
          <Group
            gap={6}
            wrap="nowrap"
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 2,
            }}
          >
            {card.foundingMember ? (
              <Badge size="sm" variant="filled" color="sun" aria-label="Founding venue">
                Founding
              </Badge>
            ) : card.foundingHostAccount ? (
              <Badge size="sm" variant="filled" color="sun" aria-label="Founding host">
                Founding host
              </Badge>
            ) : null}
            {card.verified && (
              <Badge size="sm" variant="filled" color="tide" aria-label="Verified venue">
                Verified
              </Badge>
            )}
          </Group>
        )}
      </Card.Section>
      <Stack gap="xs" p="md" style={{ flex: 1 }}>
        <Title order={4} lh={1.2} style={{ wordBreak: 'break-word' }}>
          {card.name}
        </Title>
        <Text size="sm" c="dimmed">
          {place}
        </Text>
        {card.summary && (
          <Text size="sm" lineClamp={2}>
            {card.summary}
          </Text>
        )}
        <Group gap="xs" mt="auto" wrap="wrap">
          <Text size="xs" c="dimmed">
            Capacity {card.capacityMin}–{card.capacityMax}
          </Text>
          {card.stipendOffered && (
            // Build Brief §6 — was Mantine 'grape' (purple, off-brand). Brand
            // coral aligns with the rest of the card chrome (and avoids green,
            // per §2). Final colour to be confirmed with the brand pack.
            <Badge size="xs" variant="dot" color="brand">
              Stipend
            </Badge>
          )}
          {card.openEventsCount > 0 && (
            <Badge size="xs" variant="light" color="brand">
              {card.openEventsCount} open{' '}
              {card.openEventsCount === 1 ? 'event' : 'events'}
            </Badge>
          )}
          {card.reviewAverage !== null && (
            <Text size="xs" c="dimmed">
              ★ {card.reviewAverage.toFixed(1)} ({card.reviewCount})
            </Text>
          )}
        </Group>
        {matchReasons && matchReasons.length > 0 && (
          <Text size="xs" c="dimmed">
            {matchReasons.join(' · ')}
          </Text>
        )}
      </Stack>
    </Card>
    </Link>
  );
}

function ResultsSkeleton(): React.JSX.Element {
  return (
    <Grid gap="md">
      {Array.from({ length: 6 }).map((_, i) => (
        <Grid.Col key={i} span={{ base: 12, sm: 6, lg: 4 }}>
          <Card withBorder padding="0">
            <Skeleton height={180} radius={0} />
            <Stack gap="xs" p="md">
              <Skeleton height={16} width="60%" />
              <Skeleton height={12} width="40%" />
              <Skeleton height={12} width="80%" />
            </Stack>
          </Card>
        </Grid.Col>
      ))}
    </Grid>
  );
}

function EmptyResults({ verifiedOnly }: { verifiedOnly: boolean }): React.JSX.Element {
  return (
    <Paper p="xl" withBorder>
      <Stack gap="xs" align="center">
        <Text fw={500}>
          {verifiedOnly
            ? 'No verified venues match those filters yet.'
            : 'No venues match. Try a wider city radius or fewer art forms.'}
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          New spaces join every week. Check back soon.
        </Text>
      </Stack>
    </Paper>
  );
}

function MapView({
  venues,
  cardsInBounds,
  onBoundsChange,
  mobileShowList,
  onToggleMobileList,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  verifiedOnly,
}: {
  venues: VenueCardDTO[];
  cardsInBounds: VenueCardDTO[];
  onBoundsChange: (b: { north: number; south: number; east: number; west: number }) => void;
  mobileShowList: boolean;
  onToggleMobileList: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  verifiedOnly: boolean;
}): React.JSX.Element {
  // Map only carries venues that actually have coordinates. The browse API
  // already returns lat/lng as numbers (pre-converted from lat_e6/lng_e6),
  // but the API contract permits 0/0 for venues without geocoded addresses;
  // we filter those out so they don't all stack on Null Island.
  const mapVenues = useMemo(
    () =>
      venues
        .filter((v) => Number.isFinite(v.lat) && Number.isFinite(v.lng))
        .filter((v) => !(v.lat === 0 && v.lng === 0))
        .map((v) => ({
          id: v.id,
          slug: v.slug,
          name: v.name,
          city: v.city,
          region: v.region,
          country: v.country,
          lat: v.lat,
          lng: v.lng,
          verified: v.verified,
          foundingMember: v.foundingMember,
        })),
    [venues],
  );

  return (
    <Stack gap="md">
      {/* Mobile show-list affordance — only visible at <=sm breakpoints. */}
      <Group justify="space-between" hiddenFrom="sm">
        <Text size="sm" c="dimmed">
          {mapVenues.length} {mapVenues.length === 1 ? 'venue' : 'venues'} pinned
        </Text>
        <Button
          variant="default"
          size="xs"
          onClick={onToggleMobileList}
        >
          {mobileShowList ? 'Show map' : 'Show list'}
        </Button>
      </Group>

      {/* Map: full-width, hidden on mobile when the user has chosen list.
          One VenueMap instance, not two — Mantine's hiddenFrom/visibleFrom
          uses CSS `display:none` to hide, but Leaflet still mounts inside
          a hidden subtree, doubling pin renders and tile fetches. The
          single `height` prop is responsive via clamp() so the same map
          serves desktop (~600px) and mobile (60vh, capped). */}
      {!mobileShowList && (
        <Suspense fallback={<Skeleton height={600} radius="md" />}>
          <VenueMap
            venues={mapVenues}
            onBoundsChange={onBoundsChange}
            height="clamp(420px, 60vh, 640px)"
          />
        </Suspense>
      )}

      {/* Result list under the map. On mobile we hide it unless the user
          has tapped "Show list" — keeps the map flow uncluttered at 360px. */}
      <Box visibleFrom="sm">
        <ResultList
          cards={cardsInBounds}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={onLoadMore}
          verifiedOnly={verifiedOnly}
        />
      </Box>
      {mobileShowList && (
        <Box hiddenFrom="sm">
          <ResultList
            cards={cardsInBounds}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={onLoadMore}
            verifiedOnly={verifiedOnly}
          />
        </Box>
      )}
    </Stack>
  );
}

function ResultList({
  cards,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  verifiedOnly,
}: {
  cards: VenueCardDTO[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  verifiedOnly: boolean;
}): React.JSX.Element {
  if (cards.length === 0) {
    return <EmptyResults verifiedOnly={verifiedOnly} />;
  }
  return (
    <Stack gap="md">
      <Grid gap="md">
        {cards.map((c, i) => (
          <Grid.Col key={c.id} span={{ base: 12, sm: 6, lg: 4 }}>
            <VenueCard card={c} position={i} totalLoaded={cards.length} hasFilters={false} />
          </Grid.Col>
        ))}
      </Grid>
      {hasNextPage && (
        <Center>
          <Button
            variant="default"
            onClick={onLoadMore}
            loading={isFetchingNextPage}
          >
            Load more
          </Button>
        </Center>
      )}
    </Stack>
  );
}

function FilterPanel({
  search,
  onApply,
  onClose,
}: {
  search: BrowseSearch;
  onApply: (patch: Partial<BrowseSearch>) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState<BrowseSearch>(search);

  // Mantine 7 MultiSelect parses `data` items in two distinct shapes:
  //   - flat: { value, label, disabled? }
  //   - grouped: { group: string, items: ComboboxItem[] }
  // Passing `{ value, label, group }` (a flat item with an extra `group` key)
  // makes the parser treat it as a group, then crash trying to read `.items.map`
  // on undefined — this is what surfaced WW-FUNC-400. Build a proper grouped
  // structure here, and always default to `[]` so the prop can never be
  // undefined on first render inside the Drawer.
  const artFormOptions = useMemo(() => {
    const buckets = new Map<string, { value: string; label: string }[]>();
    for (const a of ART_FORMS) {
      const arr = buckets.get(a.group) ?? [];
      arr.push({ value: a.slug, label: a.name });
      buckets.set(a.group, arr);
    }
    return Array.from(buckets.entries()).map(([group, items]) => ({
      group: ART_FORM_GROUP_LABELS[group as ArtFormGroup] ?? group,
      items,
    }));
  }, []);
  const venueTypeOptions = useMemo(() => {
    const buckets = new Map<string, { value: string; label: string }[]>();
    for (const v of VENUE_TYPES) {
      const arr = buckets.get(v.group) ?? [];
      arr.push({ value: v.slug, label: v.name });
      buckets.set(v.group, arr);
    }
    return Array.from(buckets.entries()).map(([group, items]) => ({
      group: VENUE_TYPE_GROUP_LABELS[group as VenueTypeGroup] ?? group,
      items,
    }));
  }, []);
  const vibeTagOptions = useMemo(
    () => VIBE_TAGS.map((v) => ({ value: v.slug, label: v.name })),
    [],
  );

  const apply = (): void => {
    onApply({
      art_form: draft.art_form && draft.art_form.length > 0 ? draft.art_form : undefined,
      venue_type: draft.venue_type && draft.venue_type.length > 0 ? draft.venue_type : undefined,
      vibe_tag: draft.vibe_tag && draft.vibe_tag.length > 0 ? draft.vibe_tag : undefined,
      capacity_min: draft.capacity_min,
      capacity_max: draft.capacity_max,
      verified: draft.verified,
      has_stipend: draft.has_stipend,
      sleep: draft.sleep,
    });
    onClose();
  };

  const reset = (): void => {
    const cleared: BrowseSearch = { q: search.q };
    if (search.sort) cleared.sort = search.sort;
    if (search.view) cleared.view = search.view;
    setDraft(cleared);
    onApply({
      art_form: undefined,
      venue_type: undefined,
      vibe_tag: undefined,
      capacity_min: undefined,
      capacity_max: undefined,
      verified: undefined,
      has_stipend: undefined,
      sleep: undefined,
    });
    onClose();
  };

  return (
    <Stack gap="md">
      <MultiSelect
        label="Art forms"
        description="Show venues that accept all selected forms."
        data={artFormOptions ?? []}
        value={draft.art_form ?? []}
        onChange={(v) => setDraft({ ...draft, art_form: v })}
        searchable
        clearable
      />
      <MultiSelect
        label="Venue types"
        data={venueTypeOptions ?? []}
        value={draft.venue_type ?? []}
        onChange={(v) => setDraft({ ...draft, venue_type: v })}
        searchable
        clearable
      />
      <MultiSelect
        label="Vibe"
        data={vibeTagOptions ?? []}
        value={draft.vibe_tag ?? []}
        onChange={(v) => setDraft({ ...draft, vibe_tag: v })}
        clearable
      />
      <Group grow>
        <NumberInput
          label="Min capacity"
          min={1}
          max={1000}
          value={draft.capacity_min ?? ''}
          onChange={(v) =>
            setDraft({ ...draft, capacity_min: typeof v === 'number' ? v : undefined })
          }
        />
        <NumberInput
          label="Max capacity"
          min={1}
          max={1000}
          value={draft.capacity_max ?? ''}
          onChange={(v) =>
            setDraft({ ...draft, capacity_max: typeof v === 'number' ? v : undefined })
          }
        />
      </Group>
      <Stack gap="xs">
        <Checkbox
          label="Verified only"
          checked={draft.verified === true}
          onChange={(e) => setDraft({ ...draft, verified: e.currentTarget.checked })}
        />
        <Checkbox
          label="Stipend offered"
          checked={draft.has_stipend === true}
          onChange={(e) => setDraft({ ...draft, has_stipend: e.currentTarget.checked })}
        />
        <Checkbox
          label="Place to sleep on offer"
          checked={draft.sleep === true}
          onChange={(e) => setDraft({ ...draft, sleep: e.currentTarget.checked })}
        />
      </Stack>
      <Group justify="space-between" mt="md">
        <Anchor component="button" type="button" onClick={reset}>
          Reset all
        </Anchor>
        <Button onClick={apply}>Show results</Button>
      </Group>
    </Stack>
  );
}
